import { Alert } from '@hl/ui-components';
import { forwardRef, ReactNode, useMemo } from 'react';

import { AiMessageHeader } from '../AiAvatar/AiMessageHeader';
import { useSendMessage } from '../MessageInputBox/utils/useSendMessage';
import { MessageReactions } from '../MessageReactions/MessageReactions';
import {
  MessageAlertWrapper,
  MessageWrapper,
  RetryButton,
} from './Message.styled';
import { MESSAGE_ROLE, MessageStatus } from './types/messages';
import { useMessageActions } from './utils/useMessageActions';

export interface MessageProps {
  type: MESSAGE_ROLE;
  message: string;
  renderedMessage?: ReactNode;
  renderedAdditionalActions?: ReactNode;
  renderedAdditionalUI?: ReactNode;
  id: string;
  status: MessageStatus;
  failed?: boolean;
  hideReactions?: boolean;
  assistantName?: string;
  avatarUrl?: string;
}

export const Message = forwardRef<HTMLDivElement, MessageProps>(
  (
    {
      type,
      message,
      renderedMessage = <p>{message}</p>,
      renderedAdditionalActions = <></>,
      renderedAdditionalUI = <></>,
      id,
      status = null,
      failed,
      hideReactions = false,
      assistantName,
      avatarUrl,
    },
    ref,
  ) => {
    const { handleMessageStatusChange, copyToClipboard } = useMessageActions();
    const { resendMessage } = useSendMessage();
    const plainTextMessage = useMemo(() => {
      const ele = document.createElement('div');
      ele.innerHTML = message;
      return ele.textContent ?? '';
    }, [message]);

    return (
      <MessageWrapper $type={type} role="listitem" ref={ref}>
        <div>
          {type === MESSAGE_ROLE.ASSISTANT && assistantName && (
            <AiMessageHeader
              assistantName={assistantName}
              avatarUrl={avatarUrl}
            />
          )}
          {renderedMessage}
          {type !== MESSAGE_ROLE.USER && !hideReactions && (
            <MessageReactions
              status={status}
              textToCopy={plainTextMessage}
              onUpVote={() =>
                handleMessageStatusChange(id, true).then(() => {})
              }
              onDownVote={() =>
                handleMessageStatusChange(id, false).then(() => {})
              }
              onNeutral={() =>
                handleMessageStatusChange(id, null).then(() => {})
              }
              onCopy={() =>
                copyToClipboard(plainTextMessage, id).then(() => {})
              }
              additionalActions={renderedAdditionalActions}
            />
          )}
        </div>
        {type === MESSAGE_ROLE.USER && failed && (
          <MessageAlertWrapper>
            {/* @ts-expect-error HL components is on an older react version where children is implicitly included */}
            <Alert danger={true}>
              Unable to send.
              <RetryButton onClick={() => resendMessage(message, id)}>
                Retry
              </RetryButton>
            </Alert>
          </MessageAlertWrapper>
        )}
        {renderedAdditionalUI}
      </MessageWrapper>
    );
  },
);
Message.displayName = 'Message';
